"use client"

import { useState } from "react"
import Link from "next/link"
import { ShieldCheck, CheckCircle2, Menu, X, AlertTriangle, ArrowRight, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"

interface HeaderProps {
  currentPage?: string
  showBanner?: boolean
}

interface DropdownItem {
  href: string
  label: string
}

interface NavLink {
  href?: string
  label: string
  id: string
  dropdown?: DropdownItem[]
}

export function Header({ currentPage, showBanner = true }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null)

  const navLinks: NavLink[] = [
    { href: "/#assessment", label: "Take Assessment", id: "assessment" },
    { href: "/#how-it-works", label: "How It Works", id: "how-it-works" },
    {
      label: "Industries",
      id: "industries",
      dropdown: [
        { href: "/industries/medical-practices", label: "Medical Practices" },
        { href: "/industries/dental-practices", label: "Dental Practices" },
        { href: "/industries/mental-health", label: "Mental Health" },
        { href: "/industries/healthcare-technology", label: "Healthcare Technology" },
        { href: "/industries/hospitals-health-systems", label: "Hospitals & Health Systems" },
        { href: "/industries/business-associates", label: "Business Associates" },
        { href: "/industries", label: "View All Industries" },
      ],
    },
    {
      label: "Resources",
      id: "resources",
      dropdown: [
        { href: "/guides", label: "Compliance Guides" },
        { href: "/2025-updates", label: "2025 HIPAA Updates" },
        { href: "/blog", label: "Blog" },
        { href: "/checklists/hipaa-compliance", label: "Checklists" },
        { href: "/tools/penalty-calculator", label: "Tools" },
        { href: "/faq", label: "FAQ" },
        { href: "/glossary", label: "Glossary" },
      ],
    },
    { href: "/about", label: "About", id: "about" },
    { href: "/contact", label: "Contact", id: "contact" },
  ]

  const toggleMobileDropdown = (id: string) => {
    setMobileDropdownOpen(mobileDropdownOpen === id ? null : id)
  }

  return (
    <>
      {/* Sticky Alert Banner */}
      {showBanner && (
        <div className="sticky top-0 z-50 bg-[#FEF3C7] border-b-2 border-[#F59E0B]">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-1">
                <AlertTriangle className="h-5 w-5 text-[#D97706] flex-shrink-0" />
                <div className="text-sm font-medium text-[#92400E]">
                  <span className="font-bold">⚠️ January 2025 HIPAA Security Rule Updates Now in Effect</span>
                  <span className="hidden md:inline"> — New MFA, encryption, and notification requirements.</span>
                </div>
              </div>
              <Link href="/self-assessment">
                <Button size="sm" className="bg-[#B45309] hover:bg-[#92400E] text-white whitespace-nowrap">
                  Check Your Status <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      <header className={`border-b bg-white/95 backdrop-blur-sm sticky ${showBanner ? 'top-[60px]' : 'top-0'} z-40`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="flex items-center gap-2">
              <div className="relative">
                <ShieldCheck className="h-8 w-8 text-[#0EA5E9]" />
                <CheckCircle2 className="h-4 w-4 text-[#10B981] absolute -bottom-1 -right-1 bg-white rounded-full" />
              </div>
              <span className="text-xl font-bold text-[#0F172A]">HIPAA Ready Check</span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <div key={link.id} className="relative group">
                  {link.dropdown ? (
                    <>
                      <button
                        className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                          currentPage === link.id
                            ? "text-[#0369A1]"
                            : "text-[#475569] hover:text-[#0369A1]"
                        }`}
                      >
                        {link.label}
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                        <div className="bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[240px]">
                          {link.dropdown.map((item) => (
                            <Link
                              key={item.href}
                              href={item.href}
                              className="block px-4 py-2 text-sm text-[#475569] hover:text-[#0369A1] hover:bg-gray-50 transition-colors"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      href={link.href!}
                      className={`text-sm font-medium transition-colors ${
                        currentPage === link.id
                          ? "text-[#0369A1]"
                          : "text-[#475569] hover:text-[#0369A1]"
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
              <Link
                href="/hipaa-ready-pro"
                className={`text-sm font-medium transition-colors ${
                  currentPage === "hipaa-ready-pro"
                    ? "text-[#0369A1]"
                    : "text-[#0369A1] hover:text-[#075985]"
                }`}
              >
                HIPAA Ready Pro
              </Link>
              <Link href="/self-assessment">
                <Button className="bg-[#0369A1] hover:bg-[#075985] text-white">
                  Check Compliance
                </Button>
              </Link>
            </nav>

            <button
              className="md:hidden p-2"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-[#475569]" />
              ) : (
                <Menu className="h-6 w-6 text-[#475569]" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t">
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <div key={link.id}>
                    {link.dropdown ? (
                      <>
                        <button
                          onClick={() => toggleMobileDropdown(link.id)}
                          className={`flex items-center justify-between w-full text-sm font-medium py-2 px-2 ${
                            currentPage === link.id
                              ? "text-[#0369A1]"
                              : "text-[#475569] hover:text-[#0369A1]"
                          }`}
                        >
                          {link.label}
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${
                              mobileDropdownOpen === link.id ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {mobileDropdownOpen === link.id && (
                          <div className="flex flex-col pl-4 pt-2 pb-2 gap-2">
                            {link.dropdown.map((item) => (
                              <Link
                                key={item.href}
                                href={item.href}
                                className="text-sm text-[#475569] hover:text-[#0369A1] py-1"
                                onClick={() => {
                                  setMobileMenuOpen(false)
                                  setMobileDropdownOpen(null)
                                }}
                              >
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        href={link.href!}
                        className={`block text-sm font-medium py-2 px-2 ${
                          currentPage === link.id
                            ? "text-[#0369A1]"
                            : "text-[#475569] hover:text-[#0369A1]"
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {link.label}
                      </Link>
                    )}
                  </div>
                ))}
                <Link
                  href="/hipaa-ready-pro"
                  className="block text-sm font-medium text-[#0369A1] hover:text-[#075985] py-2 px-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  HIPAA Ready Pro
                </Link>
                <Link href="/self-assessment" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="bg-[#0369A1] hover:bg-[#075985] text-white w-full mt-2">
                    Check Compliance
                  </Button>
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  )
}
